import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { getUserType, clearAuthToken, clearEmployerInfo } from '@/utils/authUtils';
import { toast } from 'sonner';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState<'employer' | 'candidate' | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkLoginStatus = () => {
      const type = getUserType();
      setIsLoggedIn(!!type);
      setUserType(type);
    };
    
    checkLoginStatus();

    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleLogout = () => {
    clearAuthToken();
    clearEmployerInfo();
    setIsLoggedIn(false);
    setUserType(null);
    toast.success('Logged out successfully');
    
    if (location.pathname === '/application-status' || location.pathname === '/dashboard') {
      navigate('/');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-white/90 backdrop-blur-md py-4'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/17fcb3e3-c3f3-4c66-b631-ee6932b1b7cb.png" 
              alt="TrueApply Logo" 
              className="h-48 mr-2" 
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-base text-apple-text hover:text-[#5271ff] transition-colors">
              Home
            </Link>
            <Link to="/jobs" className="text-base text-apple-text hover:text-[#5271ff] transition-colors">
              Jobs
            </Link>
            <Link to="/about" className="text-base text-apple-text hover:text-[#5271ff] transition-colors">
              About
            </Link>
            <Link to="/contact" className="text-base text-apple-text hover:text-[#5271ff] transition-colors">
              Contact
            </Link>

            {isLoggedIn ? (
              <div className="relative group">
                <button className="text-base text-apple-text hover:text-[#5271ff] transition-colors">
                  {userType === 'employer' ? 'Employer Account' : 'My Account'}
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {userType === 'employer' ? (
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-sm text-apple-text hover:bg-gray-100"
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <Link
                      to="/application-status"
                      className="block px-4 py-2 text-sm text-apple-text hover:bg-gray-100"
                    >
                      Application Status
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-apple-text hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/signup" className="apple-btn">
                  Sign Up
                </Link>
                <Link to="/login" className="px-5 py-2.5 rounded-full bg-[#5271ff]/80 text-white font-medium transition-all duration-300 hover:bg-opacity-90 active:scale-[0.98] active:bg-opacity-100 flex items-center justify-center">
                  Sign In
                </Link>
                <Link to="/employer-login" className="px-5 py-2.5 rounded-full bg-[#9b87f5] text-white font-medium transition-all duration-300 hover:bg-opacity-90 active:scale-[0.98] active:bg-opacity-100 flex items-center justify-center">
                  Employer Login
                </Link>
              </div>
            )}
          </nav>

          <button
            className="md:hidden text-apple-text"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="md:hidden pt-4 pb-2">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-base text-apple-text hover:text-[#5271ff] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/jobs"
                className="text-base text-apple-text hover:text-[#5271ff] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Jobs
              </Link>
              <Link
                to="/about"
                className="text-base text-apple-text hover:text-[#5271ff] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/contact"
                className="text-base text-apple-text hover:text-[#5271ff] transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>

              {isLoggedIn ? (
                <>
                  {userType === 'employer' ? (
                    <Link
                      to="/dashboard"
                      className="text-base text-apple-text hover:text-[#5271ff] transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <Link
                      to="/application-status"
                      className="text-base text-apple-text hover:text-[#5271ff] transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Application Status
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="text-base text-apple-text hover:text-[#5271ff] transition-colors text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="apple-btn"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                  <Link
                    to="/login"
                    className="px-5 py-2.5 rounded-full bg-[#5271ff]/80 text-white font-medium transition-all duration-300 hover:bg-opacity-90 active:scale-[0.98] active:bg-opacity-100 flex items-center justify-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/employer-login"
                    className="px-5 py-2.5 rounded-full bg-[#9b87f5] text-white font-medium transition-all duration-300 hover:bg-opacity-90 active:scale-[0.98] active:bg-opacity-100 flex items-center justify-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Employer Login
                  </Link>
                </>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
